import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, ShoppingBag, Trash2, Clock, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, totalItems, totalPrice, clearCart, toggleStorageUpgrade, hasStorageUpgrade } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const isLtl = location.pathname.startsWith('/letters-that-last');

  // Only show upgrade prompt when cart has real products (not just the upgrade itself)
  const hasProducts = items.some(i => i.id !== 'storage-upgrade-25yr');

  // Pick the correct checkout source based on cart contents.
  // - Cart contains ONLY the standalone Memory Vault → source=vault (digital flow → /vault-edit/<QR>)
  // - Anything else (LTL, LTL+vault add-on, etc) → source=ltl (existing physical flow, unchanged)
  const isStandaloneVaultCart = items.length > 0 && items.every((i) => i.id === 'memory-vault-standalone');
  const checkoutSource = isStandaloneVaultCart ? 'vault' : 'ltl';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[60]"
            onClick={() => setIsOpen(false)}
            data-testid="cart-backdrop"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-[#F7F5F2] z-[70] shadow-[-8px_0_40px_rgba(0,0,0,0.08)] flex flex-col"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#C6A96B]/10">
              <div className="flex items-center gap-3">
                <ShoppingBag className="text-[#C6A96B]" size={18} strokeWidth={1.5} />
                <h2 className="text-sm tracking-[0.2em] uppercase text-[#0B1F3A] font-medium" data-testid="cart-title">
                  Your Basket {totalItems > 0 && `(${totalItems})`}
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-[#6B6259] hover:text-[#0B1F3A] transition-colors"
                data-testid="cart-close-btn"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center" data-testid="cart-empty">
                  <ShoppingBag className="text-[#C6A96B]/30 mb-4" size={40} strokeWidth={1} />
                  <p className="text-sm text-[#6B6259] mb-1">Your basket is empty</p>
                  <p className="text-xs text-[#6B6259]/70">Add a keepsake to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <AnimatePresence>
                    {items.filter(i => i.id !== 'storage-upgrade-25yr').map(item => (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: 40 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white border border-[#C6A96B]/10 p-4"
                        data-testid={`cart-item-${item.id}`}
                      >
                        <div className="flex gap-4">
                          {item.image && (
                            <div className="w-16 h-16 shrink-0 bg-[#F7F5F2] border border-[#C6A96B]/6 overflow-hidden">
                              <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-xs tracking-[0.15em] uppercase text-[#C6A96B] mb-0.5">{item.collection}</p>
                                <h3 className="text-sm font-medium text-[#0B1F3A] leading-tight" data-testid={`cart-item-name-${item.id}`}>{item.name}</h3>
                                {item.options && item.options.length > 0 && (
                                  <div className="mt-1.5 space-y-0.5">
                                    {item.options.map((opt, idx) => (
                                      <p key={idx} className="text-[10px] text-[#6B6259]/80 leading-[1.5]" data-testid={`cart-option-${item.id}-${idx}`}>
                                        {opt}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => removeItem(item.id)}
                                className="text-[#6B6259]/65 hover:text-red-400 transition-colors shrink-0"
                                data-testid={`cart-remove-${item.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex items-center border border-[#C6A96B]/15">
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  className="px-2.5 py-1.5 text-[#6B6259] hover:text-[#0B1F3A] transition-colors"
                                  data-testid={`cart-qty-minus-${item.id}`}
                                >
                                  <Minus size={12} />
                                </button>
                                <span className="px-3 py-1.5 text-xs text-[#0B1F3A] font-medium min-w-[28px] text-center" data-testid={`cart-qty-${item.id}`}>
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  className="px-2.5 py-1.5 text-[#6B6259] hover:text-[#0B1F3A] transition-colors"
                                  data-testid={`cart-qty-plus-${item.id}`}
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                              <p className="text-sm font-light font-heading text-[#0B1F3A]" data-testid={`cart-item-price-${item.id}`}>
                                £{(item.price * item.quantity).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* ── Storage Upgrade Section ── Hidden for the Letters That Last™-only
                      launch. All toggle state, the 'storage-upgrade-25yr' cart line, and the
                      Stripe product remain intact; flip the flag below back to true to restore. */}
                  {false && hasProducts && (
                    <motion.div
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-2"
                      data-testid="storage-upgrade-section"
                    >
                      <div className="border-t border-[#C6A96B]/10 pt-4 mb-3">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Clock className="text-[#C6A96B]" size={13} strokeWidth={1.5} />
                          <p className="text-[10px] tracking-[0.18em] uppercase text-[#C6A96B] font-medium">Voice Message Storage</p>
                        </div>
                        <p className="text-xs text-[#6B6259] leading-[1.6] mb-3">
                          Voice messages are securely stored for 10 years as standard.
                        </p>
                      </div>
                      <button
                        onClick={toggleStorageUpgrade}
                        data-testid="storage-upgrade-toggle"
                        className={`w-full flex items-center gap-3 p-3.5 border transition-all duration-300 ${
                          hasStorageUpgrade
                            ? 'bg-[#C6A96B]/5 border-[#C6A96B]/30'
                            : 'bg-white border-[#C6A96B]/12 hover:border-[#C6A96B]/25'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-all duration-300 ${
                          hasStorageUpgrade ? 'bg-[#C6A96B] border-[#C6A96B]' : 'border-[#C6A96B]/30'
                        }`}>
                          {hasStorageUpgrade && <Check size={10} className="text-white" strokeWidth={2.5} />}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm text-[#0B1F3A]">Extend to 25 years</p>
                          <p className="text-[10px] text-[#6B6259]/70 mt-0.5">Applies to all items in this order</p>
                        </div>
                        <p className="text-sm font-light font-heading text-[#0B1F3A] shrink-0" data-testid="storage-upgrade-price">
                          + £9.99
                        </p>
                      </button>
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-[#C6A96B]/10 px-6 py-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs tracking-[0.15em] uppercase text-[#6B6259]">Subtotal</span>
                  <span className="text-lg font-light font-heading text-[#0B1F3A]" data-testid="cart-subtotal">
                    £{totalPrice.toFixed(2)}
                  </span>
                </div>
                <p className="text-[10px] text-[#6B6259]/70 tracking-wide">
                  Shipping calculated at checkout
                </p>
                <p className="text-[10px] text-[#6B6259]/60 leading-[1.55] mt-1" data-testid="cart-upload-reassurance">
                  Your voice or image can be uploaded after purchase using a secure link. You can return anytime when you're ready.
                </p>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => { setIsOpen(false); navigate(`/letters-that-last/checkout?source=${checkoutSource}`); }}
                  className="w-full bg-[#C6A96B] text-white font-medium tracking-[0.2em] uppercase text-xs py-4 transition-all duration-300 hover:bg-[#b89a5e]"
                  data-testid="cart-checkout-btn"
                >
                  Checkout
                </motion.button>
                <button
                  onClick={clearCart}
                  className="w-full text-xs text-[#6B6259]/70 hover:text-[#6B6259] transition-colors tracking-wide py-1"
                  data-testid="cart-clear-btn"
                >
                  Clear basket
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
